import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/**
 * Collects dressing geometry by material and merges it, so a building's worth
 * of skirting board, door casing and signage is a handful of draw calls rather
 * than a few thousand.
 *
 * Everything is in metres. Nothing made here is ever handed to the collision.
 */
export function createBuilder() {
  const parts = new Map();

  function add(material, geometry) {
    let list = parts.get(material);
    if (!list) parts.set(material, (list = []));
    const g = geometry.index ? geometry.toNonIndexed() : geometry;
    for (const name of Object.keys(g.attributes)) if (!['position', 'normal', 'uv'].includes(name)) g.deleteAttribute(name);
    if (!g.attributes.uv) g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(g.attributes.position.count * 2), 2));
    list.push(g);
    return g;
  }

  return {
    add,
    /** An axis-aligned box by its centre and size, turned `yaw` about its centre. */
    box(material, x, y, z, sx, sy, sz, yaw = 0) {
      const g = new THREE.BoxGeometry(Math.max(sx, 0.001), Math.max(sy, 0.001), Math.max(sz, 0.001));
      if (yaw) g.rotateY(yaw);
      g.translate(x, y, z);
      return add(material, g);
    },
    /** A flat rectangle facing `normal` ([nx, nz], horizontal), for signs and panels. */
    panel(material, x, y, z, width, height, normal, uv = null) {
      const g = new THREE.PlaneGeometry(width, height);
      if (uv) {
        const a = g.attributes.uv;
        // PlaneGeometry's corners: top-left, top-right, bottom-left, bottom-right.
        a.setXY(0, uv[0], uv[3]);
        a.setXY(1, uv[2], uv[3]);
        a.setXY(2, uv[0], uv[1]);
        a.setXY(3, uv[2], uv[1]);
      }
      g.rotateY(Math.atan2(normal[0], normal[1]));
      g.translate(x, y, z);
      return add(material, g);
    },
    /** A flat rectangle lying on the ground, a few millimetres up. */
    decal(material, x, z, width, depth, y = 0.006, yaw = 0) {
      const g = new THREE.PlaneGeometry(width, depth);
      g.rotateX(-Math.PI / 2);
      if (yaw) g.rotateY(yaw);
      g.translate(x, y, z);
      return add(material, g);
    },
    /** Merges everything into one mesh a material, under `group`. */
    build(group, { shadows = true } = {}) {
      const made = [];
      for (const [material, list] of parts) {
        if (!list.length) continue;
        const geometry = mergeGeometries(list);
        for (const g of list) g.dispose();
        const mesh = new THREE.Mesh(geometry, material);
        mesh.castShadow = shadows && !material.transparent && !material.emissiveMap;
        mesh.receiveShadow = true;
        group.add(mesh);
        made.push(mesh);
      }
      parts.clear();
      return made;
    },
  };
}
